import { faBoxOpen, faBoxesStacked, faGift } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Product } from '@shared/components/ui';
import { Badge } from '@shared/components/ui';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PackagingItem } from '../../models/Packaging';
import { DropdownActions } from '../interaction';

interface PackagingCardProps {
  item: PackagingItem;
  onClick?: (item: PackagingItem) => void;
  onEdit?: (item: PackagingItem) => void;
  onDelete?: (item: PackagingItem) => void;
  onAdjustStock?: (item: PackagingItem) => void;
  className?: string;
}

const PackagingCard = React.memo<PackagingCardProps>(
  ({ item, onClick, onEdit, onDelete, onAdjustStock, className = '' }) => {
    const { t } = useTranslation('materialAlmacenModule');

    const handleCardClick = useCallback(() => {
      onClick?.(item);
    }, [onClick, item]);

    const getBoxGroupLabel = () => {
      if (!item.packaging_box_group_id) {
        return t('card.box_group_unassigned');
      }

      if (item.box_group?.name) {
        return item.box_group.name;
      }

      return t('card.box_group_fallback', { id: item.packaging_box_group_id });
    };

    const isMarketing = item.category === 'marketing_item';
    const stock = item.stock ?? { disponible: 0, entrante: 0, reservado: 0 };
    const totalStock = (stock.disponible ?? 0) + (stock.entrante ?? 0) + (stock.reservado ?? 0);

    return (
      <div
        className={`
        dark:bg-card-bg border border-card-border rounded-xl p-4 bg-body-bg
        transition-colors
        ${className}
      `}
        onClick={handleCardClick}
        data-testid={`warehouse-material-card-${item.id}`}
      >
        {/* Información principal usando componente Product */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="mb-2 flex flex-wrap items-center gap-1">
              {/* Tipo: caja vs marketing — Warehouse material */}
              {isMarketing ? (
                <Badge
                  variant="transparent-blue"
                  className="px-2 py-[1px] text-xs w-fit font-normal rounded-full border-0 flex items-center gap-1 bg-bg-info text-info"
                >
                  <FontAwesomeIcon icon={faGift} className="w-3.5 h-3.5" />
                  <span>{t('card.category_marketing_item')}</span>
                </Badge>
              ) : (
                <Badge
                  variant="transparent-blue"
                  className="px-2 py-[1px] text-xs w-fit font-normal rounded-full border-0 flex items-center gap-1 bg-bg-warning text-warning"
                >
                  <FontAwesomeIcon icon={faBoxOpen} className="w-3.5 h-3.5" />
                  <span>{t('card.box_row_label')}</span>
                </Badge>
              )}

              <Badge
                variant="transparent-blue"
                className="px-2 py-[1px] text-xs w-fit font-normal rounded-full border-0 flex items-center gap-1 bg-bg-info text-info"
              >
                <FontAwesomeIcon icon={faBoxesStacked} className="w-3.5 h-3.5" />
                <span>{getBoxGroupLabel()}</span>
              </Badge>
            </div>
            <Product
              id={item.id}
              src={item.image_url || ''}
              name={item.name}
              code={item.code}
              acronym={item.name?.charAt(0).toUpperCase()}
              enableLineClamp={true}
              showImageModal={true}
              data-testid={`warehouse-material-name-${item.id}`}
            />
          </div>

          <div className="flex items-start gap-2">
            <DropdownActions item={item} onEdit={onEdit} onDelete={onDelete} onAdjustStock={onAdjustStock} />

            {item.status === 'inactive' && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-bg-warning text-warning mt-1 inline-block">
                {t('card.inactive')}
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3" data-testid={`warehouse-material-stock-${item.id}`}>
          <div className="bg-card-bg rounded py-1.5 text-center border border-card-border">
            <div className="text-xs mb-0.5 text-subtext">{t('card.stock.available')}</div>
            <div className="text-xs font-bold text-title">{stock.disponible ?? 0}</div>
          </div>
          <div className="bg-card-bg rounded py-1.5 text-center border border-card-border">
            <div className="text-xs mb-0.5 text-subtext">{t('card.stock.incoming')}</div>
            <div className="text-xs font-bold text-title">{stock.entrante ?? 0}</div>
          </div>
          <div className="bg-card-bg rounded py-1.5 text-center border border-card-border">
            <div className="text-xs mb-0.5 text-subtext">{t('card.stock.reserved')}</div>
            <div className="text-xs font-bold text-title">{stock.reservado ?? 0}</div>
          </div>
          <div className="bg-card-bg rounded py-1.5 text-center border border-card-border">
            <div className="text-xs mb-0.5 text-subtext">{t('card.stock.total')}</div>
            <div className="text-xs font-bold text-title">{totalStock}</div>
          </div>
        </div>
      </div>
    );
  },
);

PackagingCard.displayName = 'PackagingCard';

export default PackagingCard;
